import Swal from "sweetalert2";
import moment from "moment-timezone";
import { isEmpty } from "lodash";

export const MINIMUM_SELECT = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22,
  23, 24,
];

export const PAYMENT_METHODS = [
  { value: "card", label: "Credit Card" },
  { value: "transfer", label: "Bank Transfer" },
  { value: "paypal", label: "PayPal" },
];

export const RESERVATION_OPTIONS = [
  { value: "arrival", label: "Airport  (Arrival)" },
  { value: "departure", label: "Airport  (Departure)" },
  { value: "station", label: "Train " },
  { value: "port", label: "Port  " },
  { value: "hourly", label: "Hourly" },
  { value: "journey", label: "Journey" },
];

export const RESERVATION_TYPES = [
  "arrival",
  "departure",
  "station",
  "port",
  "hourly",
  "journey",
];

export const FIELDS_PASSENGER = [
  {
    key: "selected",
    label: "",
    sortable: false,
    class: "text-center",
    thStyle: { minWidth: "30px" },
  },
  {
    key: "fullname",
    label: "Full Name",
    sortable: true,
    thStyle: { minWidth: "150px" },
    class: "text-left",
  },
  {
    key: "phone",
    label: "Phone",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-left",
  },
  {
    key: "email",
    label: "Email",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-left",
  },
  {
    key: "type",
    label: "Type",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-center",
  },
  {
    key: "flightNumber",
    label: "Flight N°",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-center",
  },
  {
    key: "notify",
    label: "Notifications",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-center",
  },
  {
    key: "actions",
    label: "Actions",
    sortable: false,
    thStyle: { minWidth: "120px" },
    class: "text-center",
  },
];

export const FIELDS_PASSENGER_VIEW = [
  {
    key: "fullname",
    label: "Full Name",
    sortable: true,
    thStyle: { minWidth: "150px" },
    class: "text-left",
  },
  {
    key: "phone",
    label: "Phone",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-left",
  },
  {
    key: "type",
    label: "Type",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-center",
  },
  {
    key: "itinerary",
    label: "Flight N°",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-center",
  },
];

export const FIELDS_PASSENGER_VIEW_TABLE = [
  {
    key: "fullname",
    label: "Full Name",
    sortable: true,
    thStyle: { minWidth: "150px" },
    class: "text-left",
  },
  {
    key: "phone",
    label: "Phone",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-left",
  },
  {
    key: "type",
    label: "Type",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-center",
  },
  {
    key: "itinerary",
    label: "Flight N°",
    sortable: true,
    thStyle: { minWidth: "100px" },
    class: "text-center",
  },
  {
    key: "actions",
    label: "Actions",
    sortable: false,
    thStyle: { minWidth: "120px" },
    class: "text-center",
  },
];

/**
 * Check if the booking can be confirmed
 * @param {string} status - The current status of the booking
 * @param {boolean} statusUpdate - Whether the status is being updated
 * @param {string} owner - The owner of the booking
 * @returns {boolean} - True if the booking can be confirmed, false otherwise
 */
export function canConfirmBooking(status, statusUpdate, owner) {
  let values = [
    "SC",
    "SBC",
    "SCB",
    "NS",
    "DA",
    "JF",
    "POB",
    "DSR",
    "DAP",
    "DRR",
  ];
  if (owner === "admin") {
    if (statusUpdate && status === "SC") {
      return true;
    } else {
      return !values.includes(status);
    }
  }
  return false;
}

/** Check if the booking can be canceled
 * @param {string} bookingStatus - The current status of the booking
 * @param {string} owner - The owner of the booking
 * @returns {boolean} - True if the booking can be canceled, false otherwise
 */
export function canCancelBooking(bookingStatus, owner) {
  if (owner !== "admin") {
    return !(["SBC", "SCB", "NS", "JF"].indexOf(bookingStatus) > -1);
  }
  return false;
}

/** Check if the booking can be completed
 * @param {string} bookingStatus - The current status of the booking
 * @returns {boolean} - True if the booking can be completed, false otherwise
 */
export function canCompleteBooking(bookingStatus) {
  return !["SBC", "SCB", "JF", "NS"].includes(bookingStatus);
}

/** Check if the booking can be marked as no-show
 * @param {string} bookingStatus - The current status of the booking
 * @returns {boolean} - True if the booking can be marked as no-show, false otherwise
 */
export function canMarkBookingAsNoShow(bookingStatus) {
  return !["SBC", "SCB", "JF", "NS", "SRB", "POB", "DRR", "NW"].includes(
    bookingStatus
  );
}

/** Check if the booking can be deleted
 * @param {string} bookingStatus - The current status of the booking
 * @param {string} owner - The owner of the booking
 * @returns {boolean} - True if the booking can be deleted, false otherwise
 */
export function canDeleteBooking(bookingStatus, owner) {
  return owner === "admin" ? false : true;
}

/** Check if the booking can be rejected
 * @param {string} bookingStatus - The current status of the booking
 * @param {boolean} statusUpdate - Whether the status is being updated
 * @param {string} owner - The owner of the booking
 * @returns {boolean} - True if the booking can be rejected, false otherwise
 */
export function canRejectBooking(bookingStatus, statusUpdate, owner) {
  let values = [
    "SC",
    "SBC",
    "SCB",
    "NS",
    "JF",
    "DA",
    "SRB",
    "POB",
    "DAP",
    "DSR",
    "DRR",
  ];
  if (owner === "admin") {
    if (statusUpdate && bookingStatus === "SC") {
      return true;
    } else {
      return !values.includes(bookingStatus);
    }
  }
  return false;
}

/** Check if the booking email can be resent
 * @param {string} bookingStatus - The current status of the booking
 * @param {string} owner - The owner of the booking
 * @returns {boolean} - True if the booking email can be resent, false otherwise
 */
export function canResendBookingEmail(bookingStatus, owner) {
  if (owner !== "admin") {
    return ["SRB", "POB", "DRR", "NW", "DA", "SC"].includes(bookingStatus);
  }
  return false;
}

/** Calculate the difference in hours between the pickup date and the current time in the specified time zone.
 * @param {Date} pickupDate - The pickup date of the reservation.
 * @param {string} timeZone - The time zone to consider for the calculation.
 * @returns {number} - The difference in hours.
 */
export function calculateHoursDifference(pickupDate, timeZone) {
  return moment.tz(timeZone).utc(true).diff(pickupDate, "hours", true);
}

// Function to get the final list of recipients based on selected checkboxes
function getRecipients(customer, passenger) {
  let recipients = [];
  if (customer) {
    recipients.push("CUSTOMER");
  }
  if (passenger) {
    recipients.push("PASSENGER");
  }
  return recipients;
}

/** Check if all bookings can be deleted by the supplier
 * @param {Array} bookings - The list of bookings to check
 * @returns {boolean} - True if all bookings can be deleted by the supplier, false otherwise
 */
export function isDeletableBySupplier(bookings = []) {
  return bookings.every((booking) => booking.owner !== "admin");
}

/** Check if an action item can be shown based on booking status and owner
 * @param {string} bookingStatus - The current status of the booking
 * @param {string} owner - The owner of the booking
 * @returns {boolean} - True if the action item can be shown, false otherwise
 */
export function canShowActionItem(bookingStatus, owner) {
  if (owner === "admin") {
    return !["SBC", "SCB", "JF", "SC"].includes(bookingStatus);
  } else {
    return true;
  }
}

/** Confirm the booking with a SweetAlert2 dialog
 * @param {string} bookingId - The ID of the booking to confirm
 * @param {function} callback - The callback function to execute after confirmation
 */
export async function confirmBooking(bookingId, callback) {
  let { value } = await Swal.fire({
    title: "Confirm Reservation",
    html: "Are you sure you want to <strong>Confirm</strong> this reservation?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    },
  });
  if (value) {
    await callback(bookingId, "SC", "");
  }
}

/** Cancel the booking with a SweetAlert2 dialog
 * @param {string} bookingId - The ID of the booking to cancel
 * @param {function} callback - The callback function to execute after cancellation
 */
export async function cancelBooking(bookingId, callback) {
  const { value } = await Swal.fire({
    title: "Cancel Reservation",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    html:
      "<div class='space'></div>" +
      "Are you sure you want to <strong>Cancel</strong> this reservation? <div class='space'></div> To continue select a <strong>Reason:</strong>" +
      "<div class='space'></div>" +
      '<div class="col-12 mt-2 mb-3">' +
      '<label id="label-reason" class="title-label" for="select-reason">Reason</label>' +
      '<select id="select-reason" class= "input-custom" >' +
      '<option value="" disabled selected></option>' +
      '<option value="Cancelled Flight">Cancelled Flight</option>' +
      '<option value="Travel Plans Changed">Travel Plans Changed</option>' +
      '<option value="Flight Delayed">Flight Delayed</option>' +
      '<option value="Schedule Change">Schedule Change</option>' +
      '<option value="Duplicate Booking">Duplicate Booking</option>' +
      '<option value="Change of Plans">Change of Plans</option>' +
      '<option value="Incorrect Info">Incorrect Info</option>' +
      '<option value="Price Issue">Price Issue</option>' +
      '<option value="Reseller Cancellation">Reseller Cancellation</option>' +
      '<option value="Weather Issues">Weather Issues</option>' +
      '<option value="Personal Reasons">Personal Reasons</option>' +
      '<option value="Other">Other</option>' +
      "</select></div>" +
      '<div class="col-12 mb-3" id="box-other" style="display: none;">' +
      '<label id="label-other" class="title-label" for=input-other">Other Reason</label>' +
      '<textarea id="input-other" maxlength="200"  rows="1" class="input-custom" placeholder="Enter your reason"></textarea></div>',
    focusConfirm: false,
    inputPlaceholder: "Select a reason",
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    allowOutsideClick: false,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    didOpen: () => {
      const selectElement = document.getElementById("select-reason");
      const otherInputElement = document.getElementById("box-other");
      selectElement.addEventListener("change", () => {
        otherInputElement.style.display =
          selectElement.value === "Other" ? "block" : "none";
      });
    },
    preConfirm: () => {
      try {
        var selectReason = document.getElementById("select-reason").value;
        var otherReason = Swal.getPopup().querySelector("#input-other").value;

        if (!selectReason) {
          document.getElementById("label-reason").style.color = "red";
          document.getElementById("select-reason").style.border =
            "1px solid red";
          Swal.showValidationMessage("Select a reason");
          return false;
        }
        if (selectReason === "Other" && !otherReason) {
          document.getElementById("label-other").style.color = "red";
          document.getElementById("input-other").style.border = "1px solid red";
          Swal.showValidationMessage("Enter a reason");
          return false;
        }
        return selectReason === "Other" ? otherReason : selectReason;
      } catch (error) {
        return "";
      }
    },
  });
  if (value) {
    await callback(bookingId, value, null);
  }
}

/** Complete the booking with a SweetAlert2 dialog
 * @param {string} bookingId - The ID of the booking to complete
 * @param {function} callback - The callback function to execute after completion
 */
export async function completeBooking(bookingId, callback) {
  let { value } = await Swal.fire({
    title: "Complete Reservation",
    html: "Are you sure you want to mark this reservation as <br><strong>Completed</strong>?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    },
  });
  if (value) {
    await callback(bookingId, "JF", "");
  }
}

/** Mark the booking as no-show with a SweetAlert2 dialog
 * @param {string} bookingId - The ID of the booking to mark as no-show
 * @param {function} callback - The callback function to execute after marking as no-show
 */
export async function noShowBooking(bookingId, callback) {
  const { value } = await Swal.fire({
    title: "No Show Reservation",
    html: "Are you sure you want to mark this reservation as <br><strong>No Show</strong>?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    },
  });
  if (value) {
    await callback(bookingId, "NS", "Passenger didn't show");
  }
}

/** Resend booking notifications with a SweetAlert2 dialog
 * @param {string} bookingId - The ID of the booking to resend notifications for
 * @param {function} callback - The callback function to execute after resending notifications
 */
export async function resendBookingNotifications(bookingId, callback) {
  let { value } = await Swal.fire({
    title: "Resend Notification",
    html:
      "Are you sure you want to <strong>resend a notification?</strong> <div class='space'></div> To continue select who you want to send the email to:" +
      '<div class="text-left pl-2 pt-2"><div class="custom-control custom-checkbox"> <input class="custom-control-input" type="checkbox" id="customer-user" name="options"><label for="customer-user" class="custom-control-label">Customer</label></div> <div class="custom-control custom-checkbox"><input class="custom-control-input" type="checkbox" id="passenger-user" name="options"><label for="passenger-user" class="custom-control-label">Passenger</label></div></div>',
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    preConfirm: () => {
      var customer = Swal.getPopup().querySelector("#customer-user").checked;
      var passenger = Swal.getPopup().querySelector("#passenger-user").checked;
      return getRecipients(customer, passenger);
    },
  });
  if (value) {
    await callback(bookingId, value);
  }
}

/** Delete the booking with a SweetAlert2 dialog
 * @param {string} bookingId - The ID of the booking to delete
 * @param {function} callback - The callback function to execute after deletion
 */
export async function deleteBooking(bookingId, callback) {
  let { value } = await Swal.fire({
    title: "Delete Reservation",
    html: "Are you sure you want to <strong>Delete</strong> this Reservation?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/warning.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    },
  });
  if (value) {
    await callback(bookingId);
  }
}

/** Bulk delete bookings with a SweetAlert2 dialog
 * @param {Array} bookings - The list of bookings to delete
 * @param {function} callback - The callback function to execute after deletion
 */
export async function bulkDeleteBookings(bookings, callback) {
  let { value } = await Swal.fire({
    title: "Delete Reservations",
    html: "Are you sure you want to <strong>Delete</strong> the selected Reservations?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/warning.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    },
  });
  if (value) {
    await callback(bookings);
  }
}

/** Get airport details based on booking type and options.
 * @param {string} bookingType - The type of booking ("arrival" or "departure").
 * @param {Array} optionsFrom - The array of options for the "from" location.
 * @param {Array} optionsTo - The array of options for the "to" location.
 * @returns {Object|null} - The metadata of the relevant option or null if not found.
 */
export function getAirportDetails(bookingType, optionsFrom, optionsTo) {
  if (bookingType === "arrival") {
    let [optionFrom] = optionsFrom;
    if (optionFrom) {
      return !isEmpty(optionFrom.metadata) ? optionFrom.metadata : null;
    }
    return null;
  } else if (bookingType === "departure") {
    let [optionTo] = optionsTo;
    if (optionTo) {
      return !isEmpty(optionTo.metadata) ? optionTo.metadata : null;
    }
    return null;
  } else {
    return null;
  }
}

/**
 * Determine flight type based on booking type
 * @param {String} bookingType - The type of booking (arrival/departure)
 * @returns {String} - The flight type (ARRIVAL/DEPARTURE/NONE)
 */
export function getFlightType(bookingType) {
  if (bookingType === "arrival") {
    return "ARRIVAL";
  } else if (bookingType === "departure") {
    return "DEPARTURE";
  }
  return "NONE";
}

/** Populate "from" options based on booking type and location data.
 * @param {string} from - The "from" location.
 * @param {string} type - The booking type ("arrival" or "departure").
 * @param {Object} location - The location data object.
 * @returns {Array} - An array of populated "from" options.
 */
export function populateFromOptions(from, type, location) {
  if (from) {
    let isArrival = type === "arrival" && location;
    if (isArrival) {
      return [
        {
          select: from,
          type: location.type,
          country_code: location.country_code,
          location: location.location,
          place_id: location.place_id,
          owner: location.owner,
          metadata: location.metadata ? location.metadata : {},
        },
      ];
    }
    return [
      {
        select: from,
        type: ["airport", "arrival"].includes(type) ? "airport" : "",
        country_code: "",
        metadata: {},
      },
    ];
  }
  return [];
}

/** Populate "to" options based on booking type and location data.
 * @param {string} to - The "to" location.
 * @param {string} type - The booking type ("arrival" or "departure").
 * @param {Object} location - The location data object.
 * @returns {Array} - An array of populated "to" options.`
 */
export function populateToOptions(to, type, location) {
  if (to) {
    let isDeparture = type === "departure" && location;
    if (isDeparture) {
      return [
        {
          select: to,
          type: location.type,
          country_code: location.country_code,
          location: location.location,
          place_id: location.place_id,
          owner: location.owner,
          metadata: location.metadata ? location.metadata : {},
        },
      ];
    }
    return [
      {
        select: to,
        type: ["airport", "departure"].includes(type) ? "airport" : "",
        country_code: "",
        metadata: {},
      },
    ];
  }
  return [];
}
/** Get airport name based on booking type.
 * @param {string} bookingType - The type of booking ("arrival" or "departure").
 * @param {string} from - The "from" location.
 * @param {string} to - The "to" location.
 * @returns {string} - The airport name.
 */
export function getAirportName(bookingType, from, to) {
  if (bookingType === "arrival") {
    return from ? from : "";
  } else if (bookingType === "departure") {
    return to ? to : "";
  }
  return "";
}

/** Get label of the transport reference according the booking type.
 * @param {string} bookingType - The type of booking ("arrival", "departure","station", "port").
 * @returns {string} - The transport reference label.
 */
export function getLabelTransport(bookingType) {
  if (["airport", "arrival", "departure"].indexOf(bookingType) > -1) {
    return "Flight N°";
  } else if (bookingType === "station") {
    return "Rail Line";
  } else if (bookingType === "port") {
    return "Cruise Ship";
  } else if (bookingType === "hourly") {
    return "Hours";
  }
  return "";
}

/** Get transport reference according the booking type.
 * @param {string} booking - The  booking all data
 * @returns {string} - The transport reference number according to booking type ("arrival", "departure","station", "port").
 */
export function getTransportReference(booking) {
  if (["airport", "arrival", "departure"].includes(booking.type)) {
    return booking.flight_number;
  } else if (booking.type === "station") {
    return booking.number_stations;
  } else if (booking.type === "port") {
    return booking.cruise_line;
  } else if (booking.type === "hourly") {
    return `${booking.number_hourly} hours`;
  }
  return "";
}

/** Get the display format for a time unit.
 * @param {string} unit - The time unit ("MINUTES", "HOURS", "DAYS").
 * @returns {string} - The display format for the unit.
 */
export function getUnitFormat(unit) {
  if (unit === "HOURS") {
    return "hours";
  } else if (unit === "DAYS") {
    return "days";
  } else {
    return "months";
  }
}
